import { useTranslation } from 'react-i18next';
import type { Finding } from '@complan/contracts';
import { resolveFindingMessage } from '@/shared/i18n';

/**
 * The findings of one tier, as catalogue-resolved text (R18.9).
 *
 * Shared by the two Manpower editors, which each carried a byte-identical copy.
 * They are not using `features/admin/FindingsAlert` because that component owns its
 * own `Alert` chrome and admin-specific title, whereas both Manpower editors sort
 * findings into two tiers first — a destructive Alert for the blocking ones and a
 * warning Alert carrying the acknowledgement that re-submits (R3.6, R2.5) — and
 * need only the list inside each.
 *
 * A finding's text resolves by its `messageKey`, with the finding's own `detail`
 * supplied as interpolation variables and a server-supplied override winning when
 * present. Nothing here re-derives a verdict: the server decided, this surfaces.
 */
export function FindingList({
  findings,
}: {
  readonly findings: readonly Finding[];
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <ul className="list-none">
      {findings.map((finding, index) => (
        <li key={`${finding.ruleId}-${String(index)}`} className="text-[11.5px]">
          {resolveFindingMessage(t, {
            messageKey: finding.messageKey,
            messageOverride: finding.message,
            ...(finding.detail !== null && { detail: finding.detail }),
          })}
        </li>
      ))}
    </ul>
  );
}
